/**
 * Unified Conversations Hook
 * React Query hook for the "All" tab showing all conversation types
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  archiveConversation,
  getConnectionContext,
  listAllConversations,
  listArchivedConversations,
  unarchiveConversation,
} from "../services/unified-chat.service"

export function useAllConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["all-conversations", page, limit],
    queryFn: () => listAllConversations(page, limit),
    staleTime: 30 * 1000,
  })
}

export function useArchivedConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["archived-conversations", page, limit],
    queryFn: () => listArchivedConversations(page, limit),
    staleTime: 30 * 1000,
  })
}

// The detail-level caches (`dm-conversation`, `ritual-group-chat`) carry
// the per-user `isArchived` flag that drives the chat-header Archive
// vs Unarchive menu item, so they have to be invalidated too -- the
// list invalidations alone leave the open chat showing the wrong
// action until staleTime expires.
const invalidateConversationCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
) => {
  queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
  queryClient.invalidateQueries({ queryKey: ["ritual-group-chats"] })
  queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
  queryClient.invalidateQueries({ queryKey: ["archived-conversations"] })
  queryClient.invalidateQueries({ queryKey: ["dm-conversation", conversationId] })
  queryClient.invalidateQueries({ queryKey: ["ritual-group-chat", conversationId] })
}

export function useArchiveConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => archiveConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      invalidateConversationCaches(queryClient, conversationId)
    },
  })
}

export function useUnarchiveConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => unarchiveConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      invalidateConversationCaches(queryClient, conversationId)
    },
  })
}

export function useConnectionContext(userId: string | undefined) {
  return useQuery({
    queryKey: ["connection-context", userId],
    queryFn: () => getConnectionContext(userId as string),
    enabled: !!userId,
    staleTime: 60 * 1000,
  })
}
